import { CustomField } from "types/CustomField";
import { Product } from "types/Product";
import { Item } from "types/Order";

/** Template id from API may be a plain string or a populated `{ id }` (Waterline). */
export function normalizeBuildPdfTemplateId(
  ref: string | { id?: string } | undefined | null
): string {
  if (ref == null || ref === "") return "";
  if (typeof ref === "object" && ref !== null) {
    if (ref.id != null) return String(ref.id);
    return "";
  }
  return String(ref);
}

export type EditBranch = "template" | "system-template" | "none";

/**
 * Determines which branch the fetchData edit flow should enter.
 * - "template": file has a templateId — load template + custom fields
 * - "system-template": no templateId but businessTripFileType is set — load system fields
 * - "none": neither condition met — skip field loading
 */
export function resolveEditBranch(
  templateId: string | undefined | null,
  businessTripFileType: string | undefined,
  companyId: string | undefined,
  orderAmendmentFileType?: string | undefined
): EditBranch {
  if (templateId) return "template";
  if (businessTripFileType && companyId) return "system-template";
  if (orderAmendmentFileType && companyId) return "system-template";
  return "none";
}

export type OrderItem = {
  id?: string;
  [key: string]: any;
};

export interface PostData {
  templateId: string;
  date: string;
  customerId: string;
  customFields: { [key: string]: string | string[] };
  products: Product[];
  productGroups?: any[];
  fileId?: string;
  signersInfo?: {};
  address?: string[];
  fileTypeContext?: string;
  user?: string;
  order?: string;
  fuelReport?: string;
  businessTrip?: string;
  selectedTabUser?: string;
  financeId?: string;
  businessTripFileType?: string;
  orderAmendmentFileType?: string;
  fuelReportFileType?: string;
  /** Sent to build-pdf so the API can apply parent series/number + subNumber for amendments */
  isOrderAmendment?: boolean;
}

export function transformOrderItem(dbItem: Item): OrderItem {
  const unitPrice = parseFloat((dbItem as any).unitPrice ?? 0);
  const quantity = parseFloat((dbItem as any).quantity ?? 0);

  return {
    id: dbItem.id,
    title: (dbItem as any).productTitle,
    price: unitPrice,
    quantity,
    product: dbItem.product,
    notes: dbItem.description ?? undefined,
    sku: dbItem.sku ?? "",
    finalPrice: unitPrice * quantity,
  };
}

export function transformProductGroup(pg: any) {
  const quantity = Number(pg.quantity ?? 0);
  const price = Number(pg.unitPrice ?? 0);
  return {
    id: pg.id,
    productGroup: pg.productGroup?.id || pg.productGroup,
    quantity,
    price,
    notes: pg.description ?? "",
    title: pg.productGroup?.name,
    code: pg.productGroup?.code || "",
    finalPrice: quantity * price,
  };
}

export function getDatePart(dateString: string): string {
  if (dateString.includes("T")) {
    return dateString.split("T")[0];
  }
  return dateString;
}

export function getStringValue(field: CustomField): string {
  if (field.type === "file") {
    return "";
  }
  return Array.isArray(field.value) ? "" : field.value || "";
}

export function parseNumericFieldValue(
  field: Pick<CustomField, "value">
): number {
  const value = field.value as unknown;
  const raw =
    typeof value === "string"
      ? value
      : Array.isArray(value)
      ? ""
      : String(value ?? "");
  return raw.trim() === "" ? NaN : parseFloat(raw);
}

// A field with showWhen is shown, validated and sent only while the field named by showWhen.label has exactly that value.
// Not chained: the controlling field's own visibility is not checked.
export function isCustomFieldShown(
  field: CustomField,
  fields: CustomField[]
): boolean {
  const { showWhen } = field;
  if (!showWhen) return true;
  const controllingField = fields.find((f) => f.label === showWhen.label);
  if (!controllingField) return false;
  return controllingField.value === showWhen.value;
}

// The value of one field as sent to the API: empty while hidden, a number without a trailing dot ("5." -> "5").
export function customFieldPayloadValue(
  field: CustomField,
  fields: CustomField[]
): string | string[] {
  if (!isCustomFieldShown(field, fields))
    return field.type === "file" ? [] : "";
  if (field.type === "file") {
    return Array.isArray(field.value)
      ? field.value
      : field.value
      ? [field.value]
      : [];
  }
  if (field.type === "number" && typeof field.value === "string") {
    return field.value.replace(/\.$/, "");
  }
  return field.value;
}

// The custom fields of a leave request as the API takes them: by field label, each value as `customFieldPayloadValue` gives it.
export function customFieldsToPayload(
  fields: CustomField[]
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const field of fields) {
    payload[field.label] = customFieldPayloadValue(field, fields);
  }
  return payload;
}

// Keeps digits and the first decimal separator; a comma counts as a dot ("2,5" -> "2.5").
export function sanitizeNumberInput(value: string): string {
  const [integerPart, ...decimalParts] = value
    .replace(/,/g, ".")
    .replace(/[^0-9.]/g, "")
    .split(".");
  return decimalParts.length > 0
    ? `${integerPart}.${decimalParts.join("")}`
    : integerPart;
}

export function isNumberOutsideFieldRange(
  field: Pick<CustomField, "min" | "max">,
  value: number
): boolean {
  return (
    (field.min != null && value < field.min) ||
    (field.max != null && value > field.max)
  );
}

/**
 * Transform API custom field values, resolving file entities and normalising shapes.
 * The async file-fetch part is handled by the caller via the returned fileEntityKeys.
 * This pure-logic version does NOT call setFileEntitiesMap or fetchById.
 */
export function transformCustomFieldsSync(
  apiCustomFields: Record<string, any> | undefined,
  templateFields: CustomField[] | undefined
): {
  transformedFields: Record<string, any>;
  fileFieldKeys: { key: string; ids: string[] }[];
  fileEntities: { [key: string]: any[] };
} {
  if (!apiCustomFields) {
    return { transformedFields: {}, fileFieldKeys: [], fileEntities: {} };
  }

  const transformedFields: Record<string, any> = {};
  const fileEntities: { [key: string]: any[] } = {};
  const fileFieldKeys: { key: string; ids: string[] }[] = [];

  const fieldTypeMap: { [key: string]: string } = {};
  if (templateFields) {
    templateFields.forEach((field) => {
      fieldTypeMap[field.label] = field.type;
    });
  }

  for (const [key, value] of Object.entries(apiCustomFields)) {
    const isFileField = fieldTypeMap[key] === "file";
    const isProductsOrGroupsField = fieldTypeMap[key] === "products_or_groups";

    if (isProductsOrGroupsField) {
      if (typeof value === "string") {
        transformedFields[key] = value;
      } else if (
        value !== null &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        (value.products || value.productGroups)
      ) {
        transformedFields[key] = JSON.stringify(value);
      } else {
        transformedFields[key] = "";
      }
      continue;
    }

    if (Array.isArray(value)) {
      if (isFileField) {
        const firstItem = value[0];
        if (
          firstItem &&
          typeof firstItem === "object" &&
          (firstItem.id || firstItem.name || firstItem.title)
        ) {
          fileEntities[key] = value;
          transformedFields[key] = value
            .map((entity) => entity?.id)
            .filter((id) => id !== undefined && id !== null);
        } else if (firstItem && typeof firstItem === "string") {
          const fileIds = value.filter((id) => id !== undefined && id !== null);
          transformedFields[key] = fileIds;
          fileFieldKeys.push({ key, ids: fileIds });
        } else {
          transformedFields[key] = [];
          fileEntities[key] = [];
        }
      } else {
        transformedFields[key] = value
          .map((entity) => (typeof entity === "object" ? entity?.id : entity))
          .filter((id) => id !== undefined && id !== null);
      }
    } else if (
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      transformedFields[key] = value?.id;
    } else {
      transformedFields[key] = value;
    }
  }

  return { transformedFields, fileFieldKeys, fileEntities };
}

export interface ValidateFormParams {
  templateId: string;
  date: string;
  subject?: string;
  selectedContactIds: string[];
  signType: string;
  selectedUser: string;
  selectedNonTechUser: string;
  isSystemSource: boolean;
  isEditingSystemTemplate: boolean;
  isAutoBusinessTripDocument: boolean;
  customFields: CustomField[];
  products: Product[];
  productGroups: any[];
}

export type ValidationError =
  | string
  | {
      key: string;
      params: { [key: string]: string | number };
    };

function requiredFieldError(
  field: CustomField,
  defaultKey: string
): ValidationError {
  return field.requiredMessage
    ? {
        key: "custom_field_required_message",
        params: { message: field.requiredMessage },
      }
    : defaultKey;
}

export function validateForm(params: ValidateFormParams): {
  [key: string]: ValidationError;
} {
  const validationErrors: { [key: string]: ValidationError } = {};

  if (
    !params.templateId &&
    !params.isSystemSource &&
    !params.isEditingSystemTemplate
  ) {
    validationErrors["templateId"] = "template_is_required";
  }
  if (!params.date) {
    validationErrors["date"] = "date_is_required";
  }

  if (params.subject === "customer") {
    // Click-to-sign cannot sign with external contacts (backend drops Contacts
    // rows), so no counterparty contact is ever required for that sign type.
    if (params.signType !== "Click" && !params.selectedContactIds.length) {
      validationErrors["contactSigners"] = "contact_signer_is_required";
    }
    if (!params.signType) {
      validationErrors["signType"] = "sign_type_is_required";
    }
    if (!params.selectedUser) {
      validationErrors["selectedUser"] = "selected_user_is_required";
    }
    if (params.selectedUser === params.selectedNonTechUser) {
      validationErrors["selectedUserMatch"] =
        "duplicate_signer_selected_choose_a_different_user";
    }
  }

  if (!params.isAutoBusinessTripDocument) {
    params.customFields.forEach((field) => {
      if (!isCustomFieldShown(field, params.customFields)) return;

      if (field.required) {
        if (field.type === "file") {
          const fileIds = Array.isArray(field.value)
            ? field.value
            : field.value
            ? [field.value]
            : [];
          if (fileIds.length === 0) {
            validationErrors[field.label] = requiredFieldError(
              field,
              "field_is_required"
            );
          }
        } else if (field.type === "products_or_groups") {
          const hasProducts = params.products.length > 0;
          const hasGroups = params.productGroups.length > 0;
          if (!hasProducts && !hasGroups) {
            validationErrors[field.label] = requiredFieldError(
              field,
              "at_least_one_product_or_product_group_required"
            );
          }
        } else if (!field.value || field.value.length === 0) {
          validationErrors[field.label] = requiredFieldError(
            field,
            "field_is_required"
          );
        }
      }

      if (
        field.minLength &&
        field.minLength > 0 &&
        field.value &&
        (field.type === "string" || field.type === "textarea")
      ) {
        if (
          typeof field.value === "string" &&
          field.value.length < field.minLength
        ) {
          validationErrors[field.label] = {
            key: "field_must_be_at_least_characters",
            params: { minLength: field.minLength },
          };
        }
      }

      if (field.type === "number") {
        const num = parseNumericFieldValue(field);
        if (Number.isNaN(num)) {
          if (field.required) {
            validationErrors[field.label] = requiredFieldError(
              field,
              "field_is_required"
            );
          }
        } else if (isNumberOutsideFieldRange(field, num)) {
          if (field.min != null && num < field.min) {
            validationErrors[field.label] =
              field.max != null
                ? {
                    key: "must_be_between_min_and_max",
                    params: { min: field.min, max: field.max },
                  }
                : {
                    key: "must_be_at_least_min",
                    params: { min: field.min },
                  };
          } else if (field.max != null && num > field.max) {
            validationErrors[field.label] =
              field.min != null
                ? {
                    key: "must_be_between_min_and_max",
                    params: { min: field.min, max: field.max },
                  }
                : {
                    key: "must_be_at_most_max",
                    params: { max: field.max },
                  };
          }
        }
      }
    });
  }

  return validationErrors;
}

/**
 * When `FileTemplateSelect` gets `template` as a parent id, options are only child templates.
 * If state still holds the parent id, build-pdf would use the wrong HTML (e.g. finance root).
 * Call after validateForm and merge into errors under `templateId` when this returns a key.
 */
export function getParentTemplateMismatchErrorKey(params: {
  templateId: string;
  parentTemplateId: string;
  isSystemSource?: boolean;
  isEditingSystemTemplate?: boolean;
}): "select_template_from_list" | undefined {
  if (
    !params.parentTemplateId ||
    !params.templateId ||
    params.templateId !== params.parentTemplateId
  ) {
    return undefined;
  }
  if (params.isSystemSource || params.isEditingSystemTemplate) {
    return undefined;
  }
  return "select_template_from_list";
}

export interface BuildPayloadParams {
  date: string;
  templateId: string;
  customerId: string;
  customFields: CustomField[];
  products: Product[];
  productGroups: any[];
  fileId?: string;
  selectedContactIds: string[];
  selectedUser: string;
  selectedNonTechUser: string;
  signType: string;
  selectedTabUser: string;
  fileTypeContext: string;
  user?: string;
  orderId: string | null;
  fuelReportId: string | null;
  businessTripId: string | null;
  financeId?: string;
  businessTripFileType?: string;
  orderAmendmentFileType?: string;
  fuelReportFileType?: string;
  /** When true, this is an order amendment (child file); do not send order in payload */
  isOrderAmendment?: boolean;
}

export function buildPayload(params: BuildPayloadParams): PostData {
  const convertedDate = getDatePart(params.date);
  const formData: PostData = {
    date: convertedDate,
    templateId: params.templateId,
    customerId: params.customerId,
    customFields: {},
    products: params.products,
    productGroups: params.productGroups,
    fileId: params.fileId,
    signersInfo: {
      // Click-to-sign cannot sign with external contacts (backend drops
      // Contacts rows) — never send stale/previously-selected ids for it.
      contactSigners:
        params.signType === "Click"
          ? []
          : Array.from(new Set(params.selectedContactIds)),
      companyUserSigner: params.selectedUser,
      nontechuser: params.selectedNonTechUser,
      signType: params.signType,
      selectedTabUser: params.selectedTabUser,
    },
    fileTypeContext: params.fileTypeContext,
    user: params.user,
    // Do not send order for amendment (child) files; main order file may have fileId (e.g. masterFileId) and still must have order
    order: params.isOrderAmendment ? undefined : (params.orderId || undefined),
    fuelReport: params.fuelReportId || undefined,
    ...(params.financeId && { financeId: params.financeId }),
    businessTrip: params.businessTripId || undefined,
    ...(params.businessTripFileType
      ? { businessTripFileType: params.businessTripFileType }
      : {}),
    ...(params.orderAmendmentFileType
      ? { orderAmendmentFileType: params.orderAmendmentFileType }
      : {}),
    ...(params.fuelReportFileType
      ? { fuelReportFileType: params.fuelReportFileType }
      : {}),
    ...(params.isOrderAmendment === true ? { isOrderAmendment: true } : {}),
  };

  params.customFields.forEach((field) => {
    formData.customFields[field.label] = customFieldPayloadValue(
      field,
      params.customFields
    );
  });

  return formData;
}

export interface BuildPdfFormProps {
  customerId: string;
  isCustomerApproved: boolean;
  template: string | { id?: string };
  fileId: string;
  signersInfo: any;
  assignedUserId: string;
  id: string;
  subject?: string;
  user?: string;
  signType?: string;
  managerSigner?: string;
  orderEntity?: any;
  fuelReport?: any;
  year?: number;
  financeId?: string;
  businessTrip?: any;
  businessTripFileType?: string;
  lockedTemplateId?: string;
  /** When set with system company config, use registry fields + system PDF */
  orderAmendmentFileType?: string;
  /** When set with system company config, use registry fields + system PDF for fuel report */
  fuelReportFileType?: string;
  /** True when building an order amendment (child file); order must not be sent */
  isOrderAmendment?: boolean;
  /** Order id for refetching order after save (e.g. when editing amendment from order context) */
  orderId?: string;
  /** Runs after the document was saved, e.g. so a list outside redux can refresh. */
  onSuccess?: () => void;
}
